import React, { useRef } from "react";
import axios from "axios";
import "./UniversityPage.css";
import LandingForm from "../Shared/Landingform";
import { Helmet } from "react-helmet-async";
/* ─────────────────────────────────────────────
   INTAKE STRING PARSER
   The intake <select> options are plain strings like "January 2027",
   but the backend expects Intake as { monthId, monthName, year }.
───────────────────────────────────────────── */
const MONTH_NAME_TO_ID = {
  January: 1, February: 2, March: 3, April: 4, May: 5, June: 6,
  July: 7, August: 8, September: 9, October: 10, November: 11, December: 12,
};

// "January 2027" -> { monthId: 1, monthName: "January", year: 2027 }
function parseIntakeString(intakeStr) {
  if (!intakeStr) return null;
  const [monthName, yearStr] = intakeStr.trim().split(/\s+/);
  const year = parseInt(yearStr, 10);
  if (!monthName || !year || isNaN(year)) return null;
  return { monthId: MONTH_NAME_TO_ID[monthName] || null, monthName, year };
}

/* ─────────────────────────────────────────────
   DEFAULT DATA (CU Coventry — override via props)
───────────────────────────────────────────── */
const DEFAULT_UNI = {
  name:         "CU Coventry",
  fullName:     "Coventry University",
  location:     "Coventry, England",
  partnerLabel: "Official Application Partner",
  heroTitle:    "Apply to CU Coventry — guided by FlyUrDream.",
  heroSub:
    "Industry-linked teaching, strong graduate employability, and a vibrant campus in the heart of England. Apply free through our official partner channel.",
  ranks: [
    { num: "Top 50",  lbl: "UK Modern Universities" },
    { num: "£8,000",  lbl: "Min Deposit" },
    { num: "£2,000",  lbl: "Max Scholarship" },
  ],
  facts: [
    { lbl: "Location",    val: "Coventry, England" },
    { lbl: "Mode",        val: "On Campus" },
    { lbl: "Min. Deposit", val: "£8,000" },
    { lbl: "Scholarship", val: "Up to £2,000" },
  ],
  courses: [
    {
      initial: "A",
      name:    "Applied Biosciences BSc (Hons)",
      meta:    "36 Months · Undergraduate · Scholarship Available",
      fee:     "£18,300",
    },
    {
      initial: "B",
      name:    "Business Management & Leadership BA (Hons)",
      meta:    "36 Months · Undergraduate · Scholarship Available",
      fee:     "£16,800",
    },
    {
      initial: "C",
      name:    "Cloud Computing BSc (Hons)",
      meta:    "36 Months · Undergraduate · Scholarship Available",
      fee:     "£18,300",
    },
    {
      initial: "F",
      name:    "Financial Management & Accounting BA (Hons)",
      meta:    "36 Months · Undergraduate · Scholarship Available",
      fee:     "£16,800",
    },
  ],
  offerBanner: {
    icon:  "🎁",
    title: "Application Fee Waived — Apply via FlyUrDream",
    sub:   "Limited seats for the January 2027 intake",
  },
  steps: [
    {
      num: 1,
      title: "Free eligibility check",
      desc:  "We confirm your academic background fits CU Coventry's entry requirements.",
    },
    {
      num: 2,
      title: "Document & SOP support",
      desc:  "We help prepare your transcripts, SOP, and English test scores.",
    },
    {
      num: 3,
      title: "Application submitted",
      desc:  "Your application is submitted directly through our partner channel.",
    },
    {
      num: 4,
      title: "Offer & visa support",
      desc:  "Once you receive your offer, we guide you through CAS and visa filing.",
    },
  ],
  faqs: [
    {
      q: "What are the entry requirements?",
      a: "Requirements vary by course — typically 60% in 12th grade for undergraduate, plus IELTS 6.0 or equivalent. We'll confirm your exact eligibility on your free call.",
    },
    {
      q: "Is there a credibility interview?",
      a: "Yes, CU Coventry requires a short credibility interview as part of the visa process. We provide free mock interview preparation.",
    },
    {
      q: "Can I apply for a scholarship?",
      a: "Yes — scholarships up to £2,000 are available for eligible students. We'll check your eligibility during the application.",
    },
  ],
  form: {
    title:       "Apply to CU Coventry",
    subtitle:    "Free application support · No agency fee",
    deadline:    "January 2027 intake — limited seats remaining",
    submitLabel: "Apply to CU Coventry →",
    trustLine:   "Official application partner · Your data is safe",
    courses: [
      "Applied Biosciences BSc (Hons)",
      "Business Management & Leadership BA (Hons)",
      "Cloud Computing BSc (Hons)",
      "Financial Management & Accounting BA (Hons)",
    ],
    intakes: ["January 2027", "May 2027", "September 2027"],
  },
};

/* ─────────────────────────────────────────────
   FORM FIELDS builder (uses uni-specific courses)
───────────────────────────────────────────── */
function buildFormFields(uniData) {
  return [
    { name: "name",          label: "Full Name",              type: "text",   placeholder: "Enter your full name",   required: true },
    { name: "email",         label: "Email",                  type: "email",  placeholder: "you@email.com",           required: true },
    { name: "phone",         label: "Phone / WhatsApp",       type: "phone",  required: true },
    {
      name: "qualification",
      label: "Highest Qualification",
      type: "select",
      placeholder: "Select qualification",
      options: ["10th", "12th / High School", "Diploma", "Bachelor's Degree", "Master's Degree", "PhD"],
    },
    {
      name: "course",
      label: "Course of Interest",
      type: "text",
        placeholder: "e.g. " + (uniData.form.courses?.[0] || "Business Management"),
       required: true,
    },
    // {
    //   name: "course",
    //   label: "Course of Interest",
    //   type: "text",
    //   placeholder: "e.g. " + (uniData.form.courses?.[0] || "Business Management"),
    //   required: true,
    // },
    {
      name: "intake",
      label: "Intake",
      type: "select",
      placeholder: "Select intake",
      options: uniData.form.intakes,
    },
  ];
}

const CONSENT_ITEMS = (uniName) => [
  {
    id: "consent-marketing-uni",
    text: `I consent to receiving information (by email, phone, or SMS/WhatsApp) about <b>${uniName}</b> programmes and admissions from FlyUrDream, in line with the <a href="/privacy-policy">Privacy Policy</a>.`,
  },
  {
    id: "consent-whatsapp-uni",
    text: "I would like to receive WhatsApp updates on my application status, deadlines, and offer letter progress.",
  },
];

/* ─────────────────────────────────────────────
   COMPONENT
   Props:
     uniData   object (shape of DEFAULT_UNI above)
     logoSrc   string  — FlyUrDream logo URL
     onSubmit  async function(formData) — optional override; if not
               provided, this component POSTs directly to CreateEnquiry
───────────────────────────────────────────── */
export default function UniversityPage({
  uniData = DEFAULT_UNI,
  logoSrc = "/logo.png",
  onSubmit,
}) {
  const formRef = useRef(null);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // ── Submit: build Enquiry payload (same shape as LPGeneral/LoanLandingPage/
  // CollegeVisitCapture/UKDestinationPage — wrapped under "enquiry" key) and
  // CREATE it against the backend. Skipped if a parent onSubmit is supplied.
  const handleSubmit = async (data) => {
    if (onSubmit) {
      await onSubmit(data);
      return;
    }

    const resolvedEmail = data.email || "";
    const resolvedPhone =
      data.phone?.number ||
      (typeof data.phone === "string" ? data.phone : "") ||
      "";
    const resolvedPhoneCode = data.phone?.code || "+91";

    if (!resolvedEmail && !resolvedPhone) {
      console.error("Neither email nor phone resolved from form data:", data);
      throw new Error("Please provide at least an email or phone number.");
    }

    // "January 2027" -> { monthId: 1, monthName: "January", year: 2027 }
    const selectedIntake = parseIntakeString(data.intake);

    const payload = {

        Enquiry_Id: null,
       CompanyId: {
  CompanyID: window.location.origin === "https://flyurdream.com" ? 160 : 2,
        UCompanyId: window.location.origin === "https://flyurdream.com" ? "GuidP160" : "GuidP2",
},
      UCompanyId: window.location.origin === "https://flyurdream.com" ? "GuidP160" : "GuidP2",
        FirstName: data.name || "",
        LastName: "",
        Mobile_Number: resolvedPhone,
        Country_Code: resolvedPhoneCode,
        Email: resolvedEmail,
        Preferred_Contact_Time: null,
        Lead_Type: { FieldType: "University" },
        Lead_Stage: { FieldType: "New" },

           Source:{

// fieldType: `${uniData.name} University Page`
fieldType: "Manual"
  },
        Source_Company: "FlyUrDream",
        CollegeVisit_CollegeName: null,
        CollegeVisit_City: null,
        CollegeVisit_Date: null,
        Batch: data.intake || "",
        Captured_By: null,
        Captured_By_System: "UniversityPage",
        Assigned_To: null,
        Converted_By: null,
        Converted_Application_Id: null,
        Is_Consented: Boolean(data["consent-marketing-uni"]),
        Consent_Via: data["consent-whatsapp-uni"] ? { FieldType: "WhatsApp" } : null,
        Consent_Financial_Share: null,
        Consent_WhatsAppUpdates: Boolean(data["consent-whatsapp-uni"]),
        Preferred_Country: null,
        Preferred_CountryName: "",
        Course_Interest: data.qualification || "",
        Study_Level: "",
       University: {
  universityId: 0,
  univ_Name: uniData.name || "string",
  univ_Description: "string",
  univ_Logo: "string",
  univ_Phone: "string",
  univ_Email: "string",
  univ_Website: "string",
  event_Type: "string",
  intakeId: 0,
  is_Active: true,
  university_Type: {
    id: 0,
    fieldType: "string",
    description: "string",
    isActive: 0,
    typeFor: "string",
    typeValue: "string",
    countryId: {
      countryID: 0,
      countryName: "string",
      countryCode: "string",
      dial: 0,
      currency_Name: "string",
      currency: "string",
      currency_Symbol: "string",
      isActive: true,
    },
    parentId: 0,
  },
  overview: "string",
  rankings: "string",
},
        University_Interest: uniData.name || "",
        Intake: selectedIntake
          ? { monthId: selectedIntake.monthId, monthName: selectedIntake.monthName, year: selectedIntake.year }
          : null,
        Loan_Amount_Range: null,
        Has_Collateral: null,
        Notes: "",
        CreatedBy: null,
        IsArchived: false,

    };

    console.log("Payload being sent:", payload);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/CreateEnquiry`,
        payload
      );
      console.log("Enquiry created:", response.data);
    } catch (error) {
      console.error("Enquiry submit failed:", error);
      throw error;
    }
  };

  return (
    <div className="uni-page">
      {/* ── HEADER ── */}
     <Helmet>
  <title>Apply to {uniData.name} | FlyUrDream</title>
  <meta name="description" content={uniData.heroSub} />
  <link rel="canonical" href={`https://flyurdream.com/university/${uniData.name.toLowerCase().replace(/\s+/g, '-')}`} />
  <meta property="og:title" content={`Apply to ${uniData.name} | FlyUrDream`} />
  <meta property="og:description" content={uniData.heroSub} />
</Helmet>

      {/* ── HERO ── */}
      <section className="uni-hero" aria-label="University hero">
         <div className="lp-header__cta lp-header__cta--navy" onClick={scrollToForm}>
         
        </div>
        <div className="uni-hero__inner">
          <div className="uni-hero__badge">🤝 {uniData.partnerLabel}</div>
          <h1 className="uni-hero__title">{uniData.heroTitle}</h1>
          <p className="uni-hero__sub">{uniData.heroSub}</p>
          {uniData.ranks?.length > 0 && (
            <div className="uni-hero__ranks">
              {uniData.ranks.map((r) => (
                <div className="uni-hero__rank" key={r.lbl}>
                  <div className="uni-hero__rank-num">{r.num}</div>
                  <div className="uni-hero__rank-lbl">{r.lbl}</div>
                </div>
              ))}
              <div className="uni-hero__rank-1" onClick={scrollToForm}>
                <div className="uni-hero__rank-num-1">  Apply Now →</div>
        
        </div>
            </div>
          )}
        </div>
      </section>

      {/* ── MAIN LAYOUT ── */}
      <div className="uni-layout">

        {/* ── CONTENT COLUMN ── */}
        <main className="uni-content-col">

          {/* About */}
          <section className="sec-block">
            <div className="sec-label">About {uniData.name}</div>
            <h2>Why students choose {uniData.name?.split(" ")[0]}.</h2>
            <p>
              {uniData.fullName} is known for industry-linked teaching, strong graduate
              employability, and a vibrant campus. FlyUrDream is an official application
              partner, giving you direct access to admissions support.
            </p>
            {uniData.facts?.length > 0 && (
              <div className="uni-fact-grid">
                {uniData.facts.map((f) => (
                  <div className="uni-fact" key={f.lbl}>
                    <div className="uni-fact__lbl">{f.lbl}</div>
                    <div className="uni-fact__val">{f.val}</div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Popular Courses */}
          <section className="sec-block">
            <div className="sec-label">Popular Courses</div>
            <h2>Programmes open for {uniData.form?.intakes?.[0] || "next intake"}.</h2>
            <div className="uni-course-list">
              {uniData.courses.map((c) => (
                <div className="uni-course-row" key={c.name}>
                  <div className="uni-course-row__badge" aria-hidden="true">
                    {c.initial}
                  </div>
                  <div className="uni-course-row__info">
                    <div className="uni-course-row__name">{c.name}</div>
                    <div className="uni-course-row__meta">{c.meta}</div>
                  </div>
                  <div className="uni-course-row__fee">{c.fee}</div>
                </div>
              ))}
            </div>

            {uniData.offerBanner && (
              <div className="uni-offer-banner">
                <div className="uni-offer-banner__icon" aria-hidden="true">
                  {uniData.offerBanner.icon}
                </div>
                <div>
                  <div className="uni-offer-banner__title">{uniData.offerBanner.title}</div>
                  <div className="uni-offer-banner__sub">{uniData.offerBanner.sub}</div>
                </div>
              </div>
            )}
          </section>

          {/* Application Steps */}
          <section className="sec-block">
            <div className="sec-label">Your Path to {uniData.name?.split(" ")[0]}</div>
            <h2>What happens after you apply.</h2>
            <div className="uni-step-list">
              {uniData.steps.map((step) => (
                <div className="uni-step-item" key={step.num}>
                  <div className="uni-step-item__num" aria-hidden="true">
                    {step.num}
                  </div>
                  <div>
                    <div className="uni-step-item__title">{step.title}</div>
                    <div className="uni-step-item__desc">{step.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* FAQs */}
          <section className="sec-block">
            <div className="sec-label">FAQs</div>
            <h2>Common questions about {uniData.name}.</h2>
            {uniData.faqs.map((faq, i) => (
              <div className="uni-faq-item" key={i}>
                <div className="uni-faq-item__q">{faq.q}</div>
                <div className="uni-faq-item__a">{faq.a}</div>
              </div>
            ))}
          </section>

        </main>

        {/* ── FORM SIDEBAR ── */}
        <aside ref={formRef} id="uni-form" aria-label={`Apply to ${uniData.name}`}>
          <LandingForm
            variant="purple"
            title={uniData.form.title}
            subtitle={uniData.form.subtitle}
            deadlineBadge={uniData.form.deadline}
            fields={buildFormFields(uniData)}
            submitLabel={uniData.form.submitLabel}
            trustLine={uniData.form.trustLine}
            consentItems={CONSENT_ITEMS(uniData.name)}
            onSubmit={handleSubmit}
          />
        </aside>
      </div>

      {/* ── STICKY MOBILE CTA ── */}
      {/* <div className="uni-sticky-cta" aria-hidden="true">
        <button onClick={scrollToForm}>
          {uniData.form.submitLabel}
        </button>
      </div> */}

      {/* ── FOOTER ── */}
      <footer className="uni-footer" role="contentinfo">
        FlyUrDream · Official application partner of {uniData.name}<br />
        <a href="/privacy-policy">Privacy Policy</a> ·{" "}
        <a href="/terms">Terms</a> ·{" "}
        <a href="mailto:study@flyurdream.com">study@flyurdream.com</a>
      </footer>
    </div>
  );
}